<template>
  <div class="department-list-container">
    <div class="page-header">
      <div class="page-title-block">
        <h2>部门管理</h2>
        <p class="page-subtitle">按战区与职能划分团队架构，协同跟进客户</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" @click="handleAddDepartment">
          <i class="el-icon-plus"></i> 新增部门
        </el-button>
      </div>
    </div>
    
    <!-- 搜索区域 -->
    <el-card class="search-card">
      <div class="search-form">
        <el-form :inline="true" :model="searchForm" class="search-inline-form">
          <el-form-item label="部门名称">
            <el-input v-model="searchForm.name" placeholder="请输入部门名称" clearable @input="handleSearch"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch">
              <i class="el-icon-search"></i> 搜索
            </el-button>
            <el-button @click="handleReset">
              <i class="el-icon-refresh"></i> 重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
    
    <!-- 移动端卡片视图 -->
    <div v-if="isMobile" class="department-list-mobile">
      <div 
        v-for="department in filteredDepartments" 
        :key="department.id" 
        class="department-card"
      >
        <div class="department-info">
          <h3>{{ department.name }}</h3>
          <p class="description" v-if="department.description">
            <i class="el-icon-document"></i> {{ department.description }}
          </p>
        </div>
        <div class="department-actions">
          <el-button type="success" size="mini" @click="handleEditDepartment(department)">
            编辑
          </el-button>
          <el-button type="danger" size="mini" @click="handleDeleteDepartment(department)">
            删除
          </el-button>
        </div>
      </div>
    </div>
    
    <!-- 桌面端表格视图 -->
    <el-card v-else>
      <el-table :data="filteredDepartments" style="width: 100%" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="name" label="部门名称" min-width="150"></el-table-column>
        <el-table-column prop="description" label="部门描述" min-width="200"></el-table-column>
        <el-table-column label="操作" min-width="150" fixed="right">
          <template slot-scope="scope">
            <el-button type="success" size="small" @click="handleEditDepartment(scope.row)">
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDeleteDepartment(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>
    
    <!-- 新增/编辑部门对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" :width="isMobile ? '90%' : '500px'">
      <el-form :model="departmentForm" :rules="departmentRules" ref="departmentForm" label-width="100px">
        <el-form-item label="部门名称" prop="name">
          <el-input v-model="departmentForm.name" placeholder="请输入部门名称"></el-input>
        </el-form-item>
        <el-form-item label="部门描述" prop="description">
          <el-input v-model="departmentForm.description" type="textarea" placeholder="请输入部门描述" rows="4"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'DepartmentList',
  data() {
    return {
      departments: [], // 部门列表
      filteredDepartments: [], // 过滤后的部门列表
      selectedDepartments: [], // 选中的部门
      currentPage: 1, // 当前页码
      pageSize: 10, // 每页条数
      total: 0, // 总条数
      searchForm: { // 搜索条件
        name: ''
      },
      dialogVisible: false, // 新增/编辑对话框可见性
      isEditMode: false, // 是否为编辑模式
      dialogTitle: '', // 对话框标题
      isMobile: false, // 是否为移动端
      departmentForm: {
        id: '',
        name: '',
        description: ''
      },
      departmentRules: {
        name: [
          { required: true, message: '请输入部门名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        description: [
          { max: 100, message: '长度不超过 100 个字符', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.loadDepartments()
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    // 检查屏幕尺寸
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    
    // 加载部门列表
    loadDepartments() {
      request.get('/departments')
        .then(response => {
          this.departments = response.data || response
          this.filteredDepartments = this.departments
          this.total = this.departments.length
        })
        .catch(error => {
          console.error('加载部门列表失败:', error)
          this.$message.error('加载部门列表失败')
        })
    },
    
    // 搜索部门
    handleSearch() {
      let filtered = [...this.departments]
      
      // 按部门名称搜索
      if (this.searchForm.name) {
        filtered = filtered.filter(dept => dept.name.includes(this.searchForm.name))
      }
      
      this.filteredDepartments = filtered
      this.total = filtered.length
      this.currentPage = 1 // 搜索后回到第一页
    },
    
    // 重置搜索条件
    handleReset() {
      this.searchForm = {
        name: ''
      }
      this.filteredDepartments = this.departments
      this.total = this.departments.length
      this.currentPage = 1
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pageSize = val
    },
    
    // 当前页码变化
    handleCurrentChange(val) {
      this.currentPage = val
    },
    
    // 选择部门
    handleSelectionChange(selection) {
      this.selectedDepartments = selection
    },
    
    // 新增部门
    handleAddDepartment() {
      this.isEditMode = false
      this.dialogTitle = '新增部门'
      this.resetDepartmentForm()
      this.dialogVisible = true
    },
    
    // 编辑部门
    handleEditDepartment(department) {
      this.isEditMode = true
      this.dialogTitle = '编辑部门'
      this.departmentForm = {
        id: department.id,
        name: department.name,
        description: department.description || ''
      }
      this.dialogVisible = true
    },
    
    // 删除部门
    handleDeleteDepartment(department) {
      this.$confirm(`确定要删除部门 "${department.name}" 吗？`, '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
      .then(() => {
        request.delete(`/departments/${department.id}`)
          .then(() => {
            this.$message.success('删除部门成功')
            this.loadDepartments()
          })
          .catch(error => {
            console.error('删除部门失败:', error)
            this.$message.error('删除部门失败')
          })
      })
      .catch(() => {
        this.$message.info('已取消删除')
      })
    },
    
    // 重置部门表单
    resetDepartmentForm() {
      this.departmentForm = {
        id: '',
        name: '',
        description: ''
      }
      this.$refs.departmentForm && this.$refs.departmentForm.resetFields()
    },
    
    // 提交部门表单
    handleSubmit() {
      this.$refs.departmentForm.validate((valid) => {
        if (valid) {
          if (this.isEditMode) {
            // 编辑部门
            request.put(`/departments/${this.departmentForm.id}`, this.departmentForm)
              .then(() => {
                this.$message.success('编辑部门成功')
                this.dialogVisible = false
                this.loadDepartments()
              })
              .catch(error => {
                console.error('编辑部门失败:', error)
                this.$message.error('编辑部门失败')
              })
          } else {
            // 新增部门
            request.post('/departments', this.departmentForm)
              .then(() => {
                this.$message.success('新增部门成功')
                this.dialogVisible = false
                this.loadDepartments()
              })
              .catch(error => {
                console.error('新增部门失败:', error)
                this.$message.error('新增部门失败')
              })
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.department-list-container {
  width: 100%;
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding: 14px 4px 6px;
}

.page-title-block h2 {
  margin: 0;
  font-size: 22px;
  color: #0f8f59;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #80848f;
}

.search-card {
  margin-bottom: 20px;
}

.search-inline-form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

/* 移动端卡片样式 */
.department-list-mobile {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.department-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 15px;
  transition: all 0.3s ease;
}

.department-card:hover {
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
}

.department-info h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: #333;
}

.department-info p.description {
  margin: 5px 0;
  color: #666;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.5;
}

.department-actions {
  display: flex;
  gap: 8px;
  margin-top: 15px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .department-list-container {
    padding: 10px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .search-inline-form {
    flex-direction: column;
    gap: 15px;
  }
  
  .search-inline-form .el-form-item {
    width: 100%;
  }
  
  .search-inline-form .el-input {
    width: 100%;
  }
}
</style>